// Subtitles modal component

import AnalyticsInstance from '@shared/utils/analytics';
import Direction from '../../../../shared/types/enums/direction';
import Event from '@shared/classes/event';
import Focus from '../../../../shared/types/enums/focus';
import Item from './playerLanguageModalItem';
import List from '../../sections/list/list';
import Title from '../../atoms/text/playerLanguageModalTitle';
import Track from '../../atoms/container/playerLanguageModalTrackContainer';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import { TrackType } from '@shared/types/interfaces/player';
import { mediaSelector } from '../../../../shared/store/slices/media.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { setTrack } from '@shared/store/thunks/media.thunk';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useMemo } from 'react';

/**
 * PlayerLanguageModalSubtitles component.
 * Displays a list of available subtitle tracks for selection in the player language modal.
 * Handles subtitle track selection and analytics tracking when subtitles are disabled.
 *
 * @returns {JSX.Element | null} Subtitle track selection list or null if no subtitles available
 */
export default function PlayerLanguageModalSubtitles() {
  const dispatch = useAppDispatch();
  const { tracks, subtitles } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(modalSelector);

  // Use memo to memoize the event and initial index
  const { event, initialIndex } = useMemo(() => {
    return {
      event: params?.event as Event,
      initialIndex: tracks.subtitles.findIndex(
        (item) => item.id === subtitles.id
      ),
    };
  }, [params?.event, tracks.subtitles, subtitles.id]);

  // Only render if subtitle tracks are available
  return tracks.subtitles.length ? (
    <Track>
      <Title>{TranslationManager.get('MODAL.PLAYER_LANGUAGE.SUBTITLES')}</Title>
      <List
        direction={Direction.VERTICAL}
        focus={Focus.MODAL_LANGUAGE_SUBTITLES}
        initialIndex={initialIndex}
      >
        {tracks.subtitles.map((item, index) => (
          <Item
            action={() => {
              // Track analytics when subtitles are disabled (empty ID)
              if (item.id === '') {
                AnalyticsInstance.disableSubtitles();
              }
              dispatch(
                setTrack({ event, track: item, type: TrackType.SUBTITLES })
              );
            }}
            key={index}
            index={index}
            itemFocused={!!subtitles && item.id === subtitles.id}
            text={TranslationManager.get(
              item?.language
                ? `MISC.LANGUAGES.TRANSLATED.${item.language}`
                : 'MODAL.PLAYER_LANGUAGE.DISABLED'
            )}
          />
        ))}
      </List>
    </Track>
  ) : null;
}
